import React, { createContext, useCallback, useContext, useState } from 'react'

import ICidade from 'shared/modules/cidades/domain/entities/Cidade'
import ILoteResponseData from '../domain/entities/ILoteResponseData'

import IProduto from '../domain/entities/IProduto'

interface ILastFilter {
  loteId: number
  origem: string
  destino: string
  valor: number
  raioOrigem: number
  raioDestino: number
  produto: IProduto
  lotesDirecionados?: boolean
}
interface IFiltrosContextState {
  id?: number
  setId: (id: number) => void
  origem?: string
  setOrigem: (origem: number) => void
  destino?: string
  setDestino: (destino: number) => void
  valor?: number
  setValor: (valor: number) => void
  raioOrigem?: number
  setRaioOrigem: (raioOrigem: number) => void
  raioDestino?: number
  setRaioDestino: (raioDestino: number) => void
  origemCompleta: ICidade
  setOrigemCompleta: (origemCompleta: ICidade) => void
  destinoCompleto: ICidade
  setDestinoCompleto: (destinoCompleto: ICidade) => void
  lastFilter: ILastFilter
  setLastFilter: (lastFilter: ILastFilter) => void
  lotes: ILoteResponseData[]
  setLotes: (lotes: ILoteResponseData[]) => void
  setProduto: (produto: IProduto) => void
  produto?: IProduto
  clearFilterLotes(): void
  lotesDirecionados: boolean
  setLotesDirecionados: (direcionado: boolean) => void
}

const FiltrosContext = createContext<IFiltrosContextState>(
  {} as IFiltrosContextState,
)

export function useFiltros(): IFiltrosContextState {
  const context = useContext(FiltrosContext)

  if (!context) {
    throw new Error('useFiltros must be used within an FiltrosProvider')
  }

  return context
}

export const FiltrosProvider: React.FC = ({ children }) => {
  const [id, setId] = useState(null)
  const [origem, setOrigem] = useState(null)
  const [destino, setDestino] = useState(null)
  const [valor, setValor] = useState(null)
  const [raioOrigem, setRaioOrigem] = useState(100)
  const [raioDestino, setRaioDestino] = useState(100)
  const [destinoCompleto, setDestinoCompleto] = useState({} as ICidade)
  const [origemCompleta, setOrigemCompleta] = useState({} as ICidade)
  const [lastFilter, setLastFilter] = useState<ILastFilter>({} as ILastFilter)
  const [lotes, setLotes] = useState<ILoteResponseData[]>([])
  const [produto, setProduto] = useState({} as IProduto)
  const [lotesDirecionados, setLotesDirecionados] = useState(false)

  const setFiltroOrigem = useCallback((value: number) => {
    setOrigem(value)
  }, [])

  const setFiltroDestino = useCallback((value: number) => {
    setDestino(value)
  }, [])

  const setFiltroId = useCallback((value: number | null) => {
    setId(value)
  }, [])

  const setFiltroValor = useCallback((value: number) => {
    setValor(value)
  }, [])

  const setFiltroRaioOrigem = useCallback((value: number) => {
    setRaioOrigem(value)
  }, [])

  const setFiltroRaioDestino = useCallback((value: number) => {
    setRaioDestino(value)
  }, [])

  const setFiltroDestinoCompleto = useCallback((value: ICidade) => {
    setDestinoCompleto(value)
  }, [])

  const setFiltroOrigemCompleta = useCallback((value: ICidade) => {
    setOrigemCompleta(value)
  }, [])

  const setFiltroLastFilter = useCallback((value: ILastFilter) => {
    setLastFilter(value)
  }, [])

  const setFiltroLotes = useCallback((value: ILoteResponseData[]) => {
    setLotes(value)
  }, [])

  const setFiltroProduto = useCallback((value: IProduto) => {
    setProduto(value)
  }, [])

  const setFiltroLotesDirecionados = useCallback((value: boolean) => {
    setLotesDirecionados(value)
  }, [])

  const clearFilterLotes = useCallback(() => {
    setOrigem(null)
    setDestino(null)
    setId(null)
    setValor(null)
    setRaioOrigem(100)
    setRaioDestino(100)
    setDestinoCompleto(null)
    setOrigemCompleta(null)
    setLastFilter(null)
    setLotes(null)
    setProduto(null)
    setLotesDirecionados(false)
  }, [])

  return (
    <FiltrosContext.Provider
      value={{
        id,
        setId: setFiltroId,
        origem,
        setOrigem: setFiltroOrigem,
        destino,
        setDestino: setFiltroDestino,
        valor,
        setValor: setFiltroValor,
        raioOrigem,
        setRaioOrigem: setFiltroRaioOrigem,
        raioDestino,
        setRaioDestino: setFiltroRaioDestino,
        origemCompleta,
        setOrigemCompleta: setFiltroOrigemCompleta,
        destinoCompleto,
        setDestinoCompleto: setFiltroDestinoCompleto,
        lastFilter,
        setLastFilter: setFiltroLastFilter,
        lotes,
        setLotes: setFiltroLotes,
        produto,
        setProduto: setFiltroProduto,
        lotesDirecionados,
        setLotesDirecionados: setFiltroLotesDirecionados,
        clearFilterLotes,
      }}
    >
      {children}
    </FiltrosContext.Provider>
  )
}
